// src/components/Shop/OfferModal.jsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useUnsavedReminder from "../../hooks/useUnsavedReminder";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  zIndex: 3500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const panelStyle = {
  width: "min(360px, 94vw)",
  borderRadius: 18,
  background: "rgba(18,18,20,0.96)",
  border: "1px solid rgba(255,255,255,0.12)",
  boxShadow: "0 24px 72px rgba(0,0,0,0.8)",
  padding: "16px 18px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

export default function OfferModal({
  open,
  onClose,
  onSubmit,
  defaultAmount,
  loading,
  error,
}) {
  // IMPORTANT: only use defaultAmount for initial value; keep it as a string for the input
  const [amount, setAmount] = useState(
    defaultAmount != null ? String(defaultAmount) : ""
  );
  const [isDirty, setIsDirty] = useState(false);

  const {
    shouldShowReminder,
    registerActivity,
    markSaved,
    dismissReminder,
    confirmBeforeClose,
  } = useUnsavedReminder({
    isDirty,
    onConfirmSave: () => handleSubmit(),
    idleMs: 45000,
    message:
      "Don't leave your clay half-thrown — tap Submit to save this offer.",
  });

  // When the modal is (re)opened, reset the amount ONCE.
  useEffect(() => {
    if (open) {
      setAmount(defaultAmount != null ? String(defaultAmount) : "");
      setIsDirty(false);
      dismissReminder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const num = Number(amount);
    console.log("[OfferModal] submit clicked, raw amount =", amount, "num =", num);
    if (!Number.isFinite(num) || num <= 0) return;
    markSaved();
    setIsDirty(false);
    onSubmit?.(num);
  };

  if (!open) return null;

  const handleClose = async () => {
    const ok = await confirmBeforeClose();
    if (ok) onClose?.();
  };

  return createPortal(
    <div style={overlayStyle} onClick={handleClose}>
      <div
        style={panelStyle}
        onClick={(e) => {
          // allow normal input events inside the panel
          e.stopPropagation();
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>
          Make an offer
        </div>
        <div style={{ fontSize: 12, opacity: 0.85, color: "#fff" }}>
          Enter the amount you would like to offer for this piece. This flow
          will create a marketplace offer; payment is handled separately.
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            inputMode="decimal"
            autoFocus
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              registerActivity();
              setIsDirty(true);
            }}
            style={{
              marginTop: 8,
              padding: "6px 10px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(12,12,12,0.9)",
              color: "#fff",
              fontSize: 13,
              outline: "none",
              boxShadow: "none",
              width: "100%",
            }}
            placeholder="Offer amount"
          />

          {error ? (
            <div style={{ color: "#fca5a5", fontSize: 12, marginTop: 6 }}>
              {error}
            </div>
          ) : null}

          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              fontSize: 12,
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.25)",
                background: "transparent",
                color: "#fff",
                cursor: "pointer",
              }}
              disabled={loading}
            >
              Cancel
            </button>
            <div style={{ position: "relative" }}>
              {shouldShowReminder && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "110%",
                    right: 0,
                    background: "rgba(15,15,20,0.9)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    color: "#fff",
                    padding: "6px 10px",
                    borderRadius: 12,
                    fontSize: 11,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
                    maxWidth: 220,
                    lineHeight: 1.4,
                  }}
                >
                  Almost there — tap <strong>Submit</strong> so Mudo can save
                  this for you.
                </div>
              )}
              <button
                type="submit"
                disabled={
                  loading ||
                  !Number.isFinite(Number(amount)) ||
                  Number(amount) <= 0
                }
                style={{
                  padding: "7px 14px",
                  borderRadius: 999,
                  border: "none",
                  background: loading
                    ? "linear-gradient(135deg, #6b7280, #9ca3af)"
                    : "linear-gradient(135deg, #f97316, #fb923c)",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.8 : 1,
                }}
              >
                {loading ? "Submitting..." : "Submit offer"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
